import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  Input,
  Flex,
  Box,
  Stack,
} from "@chakra-ui/react";
import { CheckoutProgress, Pedido } from "../../utils/interfaces";
import { formatBRL } from "../../utils/currency";
import { BiSolidLike, BiSolidMinusCircle } from "react-icons/bi";
import { useMutationDinheiro } from "../../hooks/pagamento/mutationDinheiro";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import { queryClient } from "../../libs/queryClient";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCheckoutStatus } from "../../hooks/pagamento/useCheckoutStatus";
import { CheckoutProgressPanel, CheckoutStayOnPageWarning } from "../../components/CheckoutProgress";
import { createCheckoutProgressOverride, mergeCheckoutProgress } from "../../utils/checkoutProgress";
import { useVisualCheckoutProgress } from "../../hooks/pagamento/useVisualCheckoutProgress";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pedido: Pedido;
  isPaymentExpired?: boolean;
}

const sanitizeCheckoutErrorMessage = (rawMessage?: string) => {
  const base = (rawMessage ?? 'Erro ao finalizar o pedido').trim();
  const cleaned = base.replace(/\s*-+\s*Request ID:.*/i, '').trim();

  if (/genericexception|buson|does not contain desired reserve|falha na confirmacao|confirmorderreserve|nao e possivel ler o retorno/i.test(cleaned.toLowerCase())) {
    return 'Não é possível confirmar uma ou mais poltronas.\nO pedido e o pagamento foram cancelados.';
  }

  return cleaned;
};

const getCheckoutErrorPayload = (error: AxiosErrorResponse) => {
  const payload = error.response?.data;

  return {
    message: payload?.data?.message || payload?.message || 'Erro desconhecido',
    code: payload?.data?.code || payload?.code,
    orderCancelled: payload?.data?.order_cancelled ?? payload?.order_cancelled,
    redirectTo: payload?.data?.redirect_to || payload?.redirect_to,
  };
};


export function PagamentoDinheiro({ isOpen, onClose, pedido, isPaymentExpired = false }: Props) {
  const [buttonDisable, setButtonDisasble] = useState(false)
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false)
  const [localProgress, setLocalProgress] = useState<ReturnType<typeof createCheckoutProgressOverride> | null>(null)
  const errorHandledRef = useRef(false)
  const completionHandledRef = useRef(false)
  const beforeUnloadHandlerRef = useRef<((event: BeforeUnloadEvent) => void) | null>(null)
  const checkoutProcessingAllowed = isOpen && isProcessingCheckout && !isPaymentExpired
  const { data: checkoutProgress } = useCheckoutStatus(pedido.id, checkoutProcessingAllowed)

  const {register, reset, handleSubmit} = useForm({
    defaultValues: {
      password: "",
    },
  })
  const mutation = useMutationDinheiro()

  const disablePageUnloadProtection = useCallback(() => {
    if (!beforeUnloadHandlerRef.current) {
      return
    }

    window.removeEventListener("beforeunload", beforeUnloadHandlerRef.current)
    beforeUnloadHandlerRef.current = null
  }, [])

  const handleClose = useCallback(() => {
    if (isProcessingCheckout) {
      return
    }

    onClose()
  }, [isProcessingCheckout, onClose])

  const onConfirm = async (data: {password: string}) => {
    if (isPaymentExpired) {
      return;
    }

    setButtonDisasble(true)
    try {
      errorHandledRef.current = false
      completionHandledRef.current = false
      setIsProcessingCheckout(true)
      setLocalProgress(
        createCheckoutProgressOverride(
          pedido.id,
          "dinheiro",
          "registering_payment",
          "Registrando pagamento",
          "Estamos validando a senha e registrando a confirmação do pagamento.",
        ),
      )
      await mutation.mutateAsync({
        id: pedido.id,
        senha: data.password,
      })
      setLocalProgress(
        createCheckoutProgressOverride(
          pedido.id,
          "dinheiro",
          "payment_registered",
          "Pagamento registrado",
          "Pagamento registrado com sucesso. Agora vamos confirmar o pedido.",
        ),
      )
  
      reset()
    }catch (err: unknown) {
      setButtonDisasble(false)
      setIsProcessingCheckout(false)
      setLocalProgress(null)
      const error = err as AxiosErrorResponse

      queryClient.invalidateQueries({ queryKey: ['pedido'], exact: false })

      const errorPayload = getCheckoutErrorPayload(error);
      const isSenhaIncorreta = errorPayload.code === 'senha_dinheiro_incorreta';
      const message = sanitizeCheckoutErrorMessage(errorPayload.message);
      const redirectTo = errorPayload.redirectTo;

      if (isSenhaIncorreta && errorPayload.orderCancelled && redirectTo) {
        disablePageUnloadProtection()
        await Swal.fire({
          title: 'Pedido cancelado',
          icon: "error",
          position: "center",
          showConfirmButton: true,
          text: message,
        });
        onClose();
        window.location.href = redirectTo;
        return;
      }

      if (redirectTo) {
        disablePageUnloadProtection()
        onClose();
        sessionStorage.setItem('gopass_redirect_error_message', message);
        window.location.href = redirectTo;
        return;
      }

      Swal.fire({
        title: isSenhaIncorreta ? 'Senha incorreta' : 'Erro ao finalizar o pedido',
        icon: "error",
        position: "center",
        showConfirmButton: true,
        text: message,
      });
    }
  }

  useEffect(() => {
    if (!checkoutProgress || !isProcessingCheckout) {
      return
    }

    if (checkoutProgress.status === "failed" && !errorHandledRef.current) {
      errorHandledRef.current = true
      completionHandledRef.current = true
      disablePageUnloadProtection()
      setIsProcessingCheckout(false)
      setButtonDisasble(false)
      setLocalProgress(null)

      Swal.fire({
        title: 'Erro ao finalizar o pedido',
        icon: "error",
        position: "center",
        showConfirmButton: true,
        text: checkoutProgress.error_message || checkoutProgress.description || 'Erro ao finalizar o pedido',
      })

      onClose()
    }
  }, [checkoutProgress, isProcessingCheckout, onClose, pedido.id])

  useEffect(() => {
    if (checkoutProgress?.step && !["waiting_payment", "registering_payment", "payment_registered"].includes(checkoutProgress.step)) {
      setLocalProgress(null)
    }
  }, [checkoutProgress?.step])

  useEffect(() => {
    if (!isOpen) {
      setIsProcessingCheckout(false)
      setButtonDisasble(false)
      setLocalProgress(null)
      errorHandledRef.current = false
      completionHandledRef.current = false
      disablePageUnloadProtection()
    }
  }, [disablePageUnloadProtection, isOpen])

  useEffect(() => {
    if (!isProcessingCheckout) {
      disablePageUnloadProtection()
      return
    }

    disablePageUnloadProtection()

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }

    beforeUnloadHandlerRef.current = handleBeforeUnload
    window.addEventListener("beforeunload", handleBeforeUnload)

    return disablePageUnloadProtection
  }, [disablePageUnloadProtection, isProcessingCheckout])

  const idleProgress = createCheckoutProgressOverride(
    pedido.id,
    "dinheiro",
    "waiting_payment",
    "Aguardando pagamento",
    "Informe a senha para registrar o pagamento e prosseguir com a confirmação do pedido.",
  )

  const handleCompleted = useCallback((progress: CheckoutProgress) => {
    if (completionHandledRef.current) {
      return
    }

    completionHandledRef.current = true
    disablePageUnloadProtection()
    setIsProcessingCheckout(false)
    setButtonDisasble(false)
    setLocalProgress(null)
    const target = progress.redirect_to || `/finalizada/${pedido.id}`
    window.location.assign(target)
  }, [disablePageUnloadProtection, pedido.id])

  const effectiveProgress = useVisualCheckoutProgress({
    pedidoId: pedido.id,
    method: "dinheiro",
    remoteProgress: checkoutProgress,
    localProgress,
    idleProgress,
    enabled: checkoutProcessingAllowed,
    onCompleted: handleCompleted,
  }) ?? (isProcessingCheckout ? mergeCheckoutProgress(checkoutProgress, localProgress) : idleProgress)

  return (
    <Modal
      size={"5xl"}
      key={"pagamento-dinheiro"}
      closeOnOverlayClick={false}
      closeOnEsc={!isProcessingCheckout}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <ModalOverlay />
        <ModalContent bg={'#f4f4f4'} maxW="1100px" borderRadius="2xl">
        <ModalHeader>PAGAMENTO DINHEIRO - DETALHES DO PAGAMENTO</ModalHeader>
        <ModalCloseButton isDisabled={isProcessingCheckout} />
        <ModalBody pb={6}>
          {!isProcessingCheckout && (
            <Box
              mt={4}
              w="100%"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="2xl"
              px={{ base: 4, md: 6 }}
              py={{ base: 4, md: 5 }}
              boxShadow="sm"
            >
              <Stack spacing={5}>
                <Flex
                  alignItems={{ base: "flex-start", md: "end" }}
                  justifyContent="space-between"
                  gap={4}
                  flexDir={{ base: "column", md: "row" }}
                  w="100%"
                >
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="0.08em">
                      VALOR TOTAL DO PEDIDO
                    </Text>
                    <Text mt={1} fontSize="sm" color="gray.600">
                      Confirme sua senha para registrar o pagamento em dinheiro.
                    </Text>
                  </Box>
                  <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" textAlign="right" color="purple.900">
                    {formatBRL(Number(pedido.valor_total))}
                  </Text>
                </Flex>

                <Flex
                  alignItems={{ base: "stretch", md: "center" }}
                  justifyContent="space-between"
                  gap={4}
                  flexDir={{ base: "column", md: "row" }}
                  w="100%"
                >
                  <Text width={{ base: "100%", md: "220px" }} fontWeight="600" color="gray.700">
                    CONFIRME SUA SENHA
                  </Text>
                  <Input
                    {...register('password', {
                      minLength: 2,
                      required: true,
                    })}
                    border="1px solid #000"
                    borderRadius="lg"
                    px={4}
                    py={3}
                    type="password"
                    variant="unstyled"
                    width="100%"
                    name="password"
                    bg="gray.50"
                    minH="52px"
                  />
                </Flex>
              </Stack>
            </Box>
          )}
          <Box mt={6} w="100%">
            <CheckoutStayOnPageWarning />
          </Box>
          <Box mt={6} w={"100%"}>
            <CheckoutProgressPanel progress={effectiveProgress} fallbackStep="waiting_payment" method="dinheiro" />
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button
            bg={"#92d050"}
            size={"lg"}
            rightIcon={<BiSolidLike size={"30px"} />}
            colorScheme="blue"
            mr={3}
            onClick={handleSubmit(onConfirm)}
            disabled={buttonDisable || isPaymentExpired || isProcessingCheckout}
            isLoading={mutation.isPending}
          >
            CONFIRMAR
          </Button>
          <Button
            bg={"#ff0000"}
            size={"lg"}
            color={"white"}
            rightIcon={<BiSolidMinusCircle size={"30px"} />}
            isDisabled={isProcessingCheckout}
            onClick={onClose}
          >
            CANCELAR
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
